import { useState, type FormEvent } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ghlConfigSchema, resendConfigSchema } from '@ventin/shared';

interface Props {
  type: 'GHL' | 'RESEND';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
}

const PROVIDER_DOCS: Record<'GHL' | 'RESEND', { title: string; helper: string; href: string }> = {
  GHL: {
    title: 'Connect GoHighLevel',
    helper: 'Generate an API key in GHL Settings → Integrations.',
    href: 'https://highlevel.stoplight.io/',
  },
  RESEND: {
    title: 'Connect Resend',
    helper: 'Create an API key at resend.com/api-keys.',
    href: 'https://resend.com/api-keys',
  },
};

export function IntegrationDialog({ type, open, onOpenChange, onSubmit }: Props) {
  const [apiKey, setApiKey] = useState('');
  const [inboundDomain, setInboundDomain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const docs = PROVIDER_DOCS[type];

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (type === 'GHL') {
        const parsed = ghlConfigSchema.parse({ apiKey });
        await onSubmit(parsed);
      } else {
        const parsed = resendConfigSchema.parse({ apiKey, inboundDomain });
        await onSubmit(parsed);
      }
      onOpenChange(false);
      setApiKey('');
      setInboundDomain('');
    } catch {
      setError("Couldn't save integration — check your API key and try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{docs.title}</DialogTitle>
          <DialogDescription>
            {docs.helper}{' '}
            <a href={docs.href} target="_blank" rel="noreferrer" className="text-primary underline">
              Provider docs
            </a>
            .
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="apiKey">API key</Label>
            <Input
              id="apiKey"
              type="password"
              autoComplete="off"
              required
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </div>
          {type === 'RESEND' && (
            <div className="space-y-2">
              <Label htmlFor="inboundDomain">Inbound domain</Label>
              <Input
                id="inboundDomain"
                type="text"
                placeholder="mail.example.com"
                required
                value={inboundDomain}
                onChange={(e) => setInboundDomain(e.target.value)}
              />
            </div>
          )}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
